import { FiBox } from 'react-icons/fi';

export const nounClausesData = {
  courseId: 'noun-clauses',
  courseType: 'grammar',
  title: 'Noun Clauses',
  icon: FiBox,
  overview: {
    impact: 'Essential for expressing opinions and reported speech',
    focus: 'Using clauses as subjects or objects',
    uses: 'Writing Task 2, Speaking Part 3',
    duration: '20 minutes',
  },
  sections: [
    {
      title: 'What Are Noun Clauses?',
      content: `<p>A noun clause is a dependent clause that functions as a noun—as a subject, object, or complement.</p>`,
      examples: [
        { label: 'Subject', text: 'What you think matters to me.' },
        { label: 'Object', text: 'I believe that education is important.' },
        { label: 'Complement', text: 'The question is whether we can afford it.' },
      ],
    },
    {
      title: 'Common Noun Clause Starters',
      content: `<p>These words introduce noun clauses:</p>`,
      points: [
        'That: I think that technology is beneficial.',
        'What: What concerns me is the cost.',
        'Whether/If: I wonder whether this is correct.',
        'How: She explained how the system works.',
        'Why/Where/When: I understand why you\'re concerned.',
      ],
    },
    {
      title: 'Noun Clauses in IELTS Writing',
      content: `<p>Noun clauses help express opinions and report others' views:</p>`,
      examples: [
        { label: 'Opinion', text: 'I believe that governments should invest in education.' },
        { label: 'Reporting', text: 'Many argue that technology has negative effects.' },
        { label: 'Emphasis', text: 'What matters most is that students develop critical thinking.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify Noun Clause Function',
      type: 'select',
      instructions: 'Identify how the noun clause functions.',
      questions: [
        {
          text: 'That she passed the exam surprised everyone.',
          options: ['Subject', 'Object', 'Complement'],
          answer: 'Subject',
          explanation: 'The clause "That she passed..." is the subject of "surprised".',
        },
        {
          text: 'I know what you mean.',
          options: ['Subject', 'Object', 'Complement'],
          answer: 'Object',
          explanation: 'The clause "what you mean" is the object of "know".',
        },
        {
          text: 'The problem is that we have no time.',
          options: ['Subject', 'Object', 'Complement'],
          answer: 'Complement',
          explanation: 'The clause follows "is" and complements "problem".',
        },
      ],
    },
  ],
  tips: [
    'Use "I believe that..." to introduce opinions',
    'Start sentences with "What..." for emphasis',
    'Practice reporting others\' views using noun clauses',
  ],
  dailyPractice: [
    'Write opinion sentences with noun clauses',
    'Practice starting sentences with "What..."',
    'Report others\' views in your essays',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar Concepts',
  aiContext: 'Noun Clauses: Dependent clauses functioning as nouns, introduced by that, what, whether, how, etc. Used for expressing opinions and reporting views.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...nounClausesData,
      title: nounClausesData.title + ' (Foundations)',
      overview: {
        ...nounClausesData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: nounClausesData.sections.slice(0, Math.max(2, Math.ceil(nounClausesData.sections.length / 2))),
      exercises: nounClausesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...nounClausesData,
      title: nounClausesData.title + ' (Advanced)',
      overview: {
        ...nounClausesData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: nounClausesData.sections,
      exercises: nounClausesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(nounClausesData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return nounClausesData;
}
